/* eslint-disable max-len */
import React, { FC, KeyboardEvent, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classes from './ListCard.module.less';
import useGoToRoute from '../../hooks/useGoToRoute';
import useGameGroup from '../../hooks/useGameGroup';
import { GAMES } from '../../constants/Games';
import { setActiveList } from '../../views/auth/slice/actions';
import { getActiveList, getUserData } from '../../views/auth/slice/selectors';
import { markListOpened } from '../../views/student-words-list/openedLists';
import { pluralForm } from '../../utils/plural';

interface ListCardProp {
  list: any;
  // Udostępniona w ostatnich 7 dniach i jeszcze nieotwarta (openedLists)
  isNew?: boolean;
}

// Karta listy na ekranie list ucznia. Cała karta jest przyciskiem: wybiera listę i otwiera pierwszą grę. Lista
// z mniej niż 2 pozycjami (ta sama granica co u nauczyciela) jest szara i nie reaguje
const ListCard: FC<ListCardProp> = ({
  list,
  isNew = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [gameGroup] = useGameGroup();
  // Pierwsza gra grupy wybranej w menu — przy Easy uczeń nie trafia do gry z Regular
  const goToFirstGame = useGoToRoute(GAMES[gameGroup][0].path);
  const activeList = useSelector(getActiveList);
  const userId = useSelector(getUserData)?.id;

  const canPlay = list.words.length >= 2;
  const isActive = activeList?.id === list.id;

  const openList = useCallback(() => {
    if (!canPlay) {
      return;
    }
    if (userId) {
      markListOpened(userId, list.id);
    }
    dispatch(setActiveList(list));
    goToFirstGame();
  }, [canPlay, userId, list, dispatch, goToFirstGame]);

  // Z klawiatury jak zwykły przycisk — tylko Enter i spacja
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openList();
    }
  }, [openList]);

  // Z prawej strony stopki stan listy — najwyżej jeden: za krótka, aktywna albo nowa
  let status = null;

  if (!canPlay) {
    status = <span className={classNames(classes.status, classes.statusTooShort)}>{t('words-list:card.tooShort')}</span>;
  } else if (isActive) {
    status = <span className={classNames(classes.status, classes.statusActive)}>{t('words-list:card.active')}</span>;
  } else if (isNew) {
    status = <span className={classNames(classes.status, classes.statusNew)}>{t('words-list:card.new')}</span>;
  }

  return (
    <div
      role="button"
      tabIndex={canPlay ? 0 : -1}
      aria-disabled={!canPlay}
      className={classNames(classes.card, {
        [classes.active]: isActive,
        [classes.tooShort]: !canPlay,
      })}
      onClick={openList}
      onKeyDown={onKeyDown}
    >
      <div className={classes.main}>
        <h3 className={classes.title} title={list.name}>{list.name}</h3>
        {list.description && <p className={classes.description}>{list.description}</p>}
      </div>

      <div className={classes.footer}>
        <span className={classes.itemsCount}>{t(`words-list:itemsCount.${pluralForm(list.words.length)}`, { n: list.words.length })}</span>
        {status}
      </div>
    </div>
  );
};

export default ListCard;
